// FE#335 — schedule WhatsApp Status ("Story") updates.
//
// A scheduled Status is modeled as an ordinary scheduled message whose
// recipient is the WhatsApp Status broadcast JID (`status@broadcast`). This
// reuses the existing scheduler/calendar/usermessage model untouched — the
// only new behavior is at FIRE TIME: instead of a chat send, the engine
// posts to Status via the already-shipped `createStatusTool` primitive
// (present on BOTH engines — extension wa-js + Baileys gateway).
//
// IMPORTANT — Status has NO per-recipient ack. `createStatusTool` returns
// only a `messageId`. A scheduled Status must therefore be a ONE-SHOT: it is
// confirmed the moment the engine returns a messageId, and it must NOT ride
// the ack-gated redispatch roster (`sent_pending_ack` / store-check), which
// would re-post it. See CLAUDE.md "retry on ack < 1" — that path is for chat
// sends only.
import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import type { UserMessage } from '@/bt-common/schemas/bt/user-message';
import type { CreateStatusParams } from '@common/tools/whatsapp/wa-tools-server-actions';
import {
  clearStatusPostMarker,
  defaultStatusLedgerStorage,
  hasStatusPostMarker,
  isPrePostFailure,
  markStatusPostAttempted,
  type StatusLedgerStorage,
} from './status-post-ledger';

/** The WhatsApp Status / "Story" broadcast JID. A scheduled message whose
 *  recipient is this sentinel is a scheduled Status post, not a chat send. */
export const WHATSAPP_STATUS_TARGET_ID = 'status@broadcast';

type TargetLike = Pick<UserMessage, 'contactList'> & { chatId?: unknown };
type AssetLike = { type?: string | null; url?: string | null; name?: string | null } | null | undefined;

/** Recipient id of a scheduled item — mirrors the send thunk's
 *  `contactList?.[0]?.id` resolution, with a `chatId` fallback. */
export function scheduledTargetId(m: TargetLike | null | undefined): string {
  const a = m as any;
  return String(a?.contactList?.[0]?.id ?? a?.chatId ?? '').trim();
}

/** True when this scheduled item targets WhatsApp Status. */
export function isScheduledStatus(m: TargetLike | null | undefined): boolean {
  return chatKindOf(scheduledTargetId(m)) === 'status';
}

/** Loose asset-kind hints the WhatsApp Status API accepts, mapped to the
 *  top-level MIME the engine's image|video|audio branch keys on. WhatsApp
 *  Status supports text, image, gif, audio and video only (no document /
 *  sticker) — those return undefined and are treated as unsupported media. */
const KIND_TO_MIME: Record<string, string> = {
  image: 'image/*',
  photo: 'image/*',
  video: 'video/*',
  gif: 'video/*', // WhatsApp GIF is an mp4 video with the GIF attribution
  audio: 'audio/*',
  voice: 'audio/*',
};

/** Resolve a top-level MIME hint for a Status media asset. Real MIMEs
 *  (`image/png`, `video/mp4`) pass through lower-cased; loose kind hints map
 *  via KIND_TO_MIME. Returns undefined for empty / unknown / unsupported. */
export function statusMediaMimeHint(asset: AssetLike): string | undefined {
  const t = String(asset?.type ?? '').toLowerCase().trim();
  if (!t) return undefined;
  if (t.includes('/')) return t; // already a MIME
  return KIND_TO_MIME[t];
}

/** A media asset is a valid Status attachment when it has a url AND its kind
 *  is one WhatsApp Status supports (image / video / gif / audio). */
export function isSupportedStatusMedia(asset: AssetLike): boolean {
  if (!asset) return false;
  const url = String(asset.url ?? '').trim();
  if (!url) return false;
  return statusMediaMimeHint(asset) !== undefined;
}

function statusText(m: UserMessage | null | undefined): string {
  return String((m as any)?.message ?? '').trim();
}

/** True when there is something to post — non-empty text OR supported media.
 *  An empty status is refused before it reaches the engine. */
export function hasPostableStatusContent(m: UserMessage | null | undefined): boolean {
  return statusText(m).length > 0 || isSupportedStatusMedia((m as any)?.asset);
}

/** Map a scheduled Status usermessage to `createStatusTool` params. The
 *  client action converts an http(s) `fileDataUrl` to a data URL itself, so
 *  the asset's GCS `url` is passed through directly. Unsupported-media assets
 *  are ignored (the status degrades to text-only). */
export function buildStatusToolParams(m: UserMessage): CreateStatusParams {
  const params: CreateStatusParams = {};
  // FE#360 — the duplicate-guard tag. The engine stamps this into the posted
  // Status's `messageSecret`, which is the SAME slot a chat send uses and the same
  // slot the shared send-protocol store-check reads back. Without it a Status is
  // invisible to the dedupe every chat send already gets.
  const id = String((m as any)?._id ?? '').trim();
  if (id) params.userMessageId = id;
  const text = statusText(m);
  if (text) {
    params.text = text;
    params.content = text;
  }
  const asset = (m as any)?.asset as AssetLike;
  if (isSupportedStatusMedia(asset)) {
    params.fileDataUrl = String(asset!.url);
    const name = String(asset!.name ?? '').trim();
    if (name) params.fileName = name;
    const mime = statusMediaMimeHint(asset);
    if (mime) params.fileMimeType = mime;
  }
  return params;
}

export interface StatusDispatchResult {
  success: boolean;
  messageId?: string;
  error?: string;
  /** FE#360 — refused by the at-most-once ledger: this engine already attempted
   *  this Status. NOT a send failure; the caller must not retry it. */
  alreadyAttempted?: boolean;
  /** FE#47 — the post went through but WhatsApp's id could not be read back;
   *  `messageId` is derived from the scheduled message (statusFallbackMessageId). */
  unverifiedId?: boolean;
}

/** FE#47 — stand-in Status id when the post succeeded but WhatsApp's id could not
 *  be read back. Prefixed so it can never collide with a real WA message id. */
export const statusFallbackMessageId = (userMessageId: string): string =>
  `bt-${userMessageId || Date.now()}`;

export interface DispatchScheduledStatusOptions {
  /** Ledger backing store. Omit for the browser-local default; pass `null` to
   *  disable the ledger (an engine with no storage of its own). */
  ledgerStorage?: StatusLedgerStorage | null;
  /** This attempt was requested by a PERSON (the retry / send-now button), not
   *  by the automatic re-dispatch the ledger exists to stop. Without this a user
   *  pressing retry on a failed Status would be silently refused and the message
   *  terminalized as sent — a button that does nothing. */
  allowRepost?: boolean;
}

type CreateStatusFn = (params: CreateStatusParams) => Promise<{
  success?: boolean;
  action?: 'text' | 'media';
  messageId?: string;
  error?: string;
}>;

/** Fire a scheduled Status through the injected engine primitive. One-shot:
 *  success is the returned messageId; there is no ack follow-up. Never
 *  throws — an empty status or an engine error becomes a failed result so the
 *  caller can terminalize (bounded attempt-count retry, NOT ack-based).
 *
 *  FE#360 — wrapped in the at-most-once ledger (status-post-ledger.ts). The
 *  messageSecret tag added alongside this lets the SHARED send-protocol dedupe
 *  see a prior Status, which covers the ordinary re-dispatch. The ledger covers
 *  the two cases that guard physically cannot:
 *    * a TEXT status carries no tag — WhatsApp's own
 *      `sendStatusTextMsgAction({color, font, text})` has no field for one;
 *    * a re-dispatch arriving before the posted message lands in this engine's
 *      store would read a miss and post again.
 *  A duplicate Status is public and permanent, so an ambiguous outcome refuses
 *  the repost rather than risking it. */
export async function dispatchScheduledStatus(
  m: UserMessage,
  createStatus: CreateStatusFn,
  options?: DispatchScheduledStatusOptions,
): Promise<StatusDispatchResult> {
  if (!hasPostableStatusContent(m)) {
    return { success: false, error: 'Empty status — nothing to post (no text or supported media)' };
  }

  const ledgerStorage = options?.ledgerStorage === undefined
    ? defaultStatusLedgerStorage()
    : options.ledgerStorage;
  const id = String((m as any)?._id ?? '').trim();
  const ledger = id ? ledgerStorage : null;

  // A person pressing "send"/"retry" is ASKING for a post; the ledger exists to
  // stop the machine from reposting on its own, not to overrule the user. Clear
  // the stale marker so this attempt runs and is itself recorded.
  if (ledger && options?.allowRepost) clearStatusPostMarker(ledger, id);

  if (ledger && !options?.allowRepost && hasStatusPostMarker(ledger, id)) {
    return {
      success: false,
      alreadyAttempted: true,
      error: 'Status already posted by this engine — refusing to post it a second time',
    };
  }

  // Marked BEFORE the post: an outcome we never get to observe must leave the
  // marker standing, or the retry re-posts (the duplicate bug).
  if (ledger) markStatusPostAttempted(ledger, id);

  try {
    const res = await createStatus(buildStatusToolParams(m));
    if (res && res.success !== false && res.messageId) {
      return { success: true, messageId: res.messageId };
    }
    // FE#47 — WhatsApp's send action returned without throwing, so the Status is live;
    // only its id could not be read back (a text status is sent with no id of ours).
    // Failing it here kept the ledger marker, so every automatic retry was refused and
    // a live Status was recorded as failed. Report delivery with an id derived from the
    // scheduled message, flagged unverified; the marker stays (no repost).
    if (res && res.success === true && !res.error) {
      return { success: true, messageId: statusFallbackMessageId(id), unverifiedId: true };
    }
    const error = res?.error || 'Status post failed (no messageId returned)';
    // Released only when the failure provably predates the post.
    if (ledger && isPrePostFailure(error)) clearStatusPostMarker(ledger, id);
    return { success: false, error };
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    if (ledger && isPrePostFailure(error)) clearStatusPostMarker(ledger, id);
    return { success: false, error };
  }
}
